import { useMemo } from "react";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import type { SortDirection } from "#app/lib/data";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { Omittable } from "@salestrak/ui";

/**
 * The card's transient, per-viewer header filters — set by the header controls
 * (Only-Me toggle + advanced filter popover) into the kit's `WidgetViewState`,
 * NEVER persisted (that's `filters`/config). The persisted config query seeds the
 * visible header filter; after the header publishes `filterWhere`, it owns the
 * query predicate for this viewer/session so clear/tweak works without writing
 * settings. Config-level `showArchived`/`dateRange` still fold from persisted
 * settings. Both keys fold centrally in `useScopedWhere` — `onlyMe` forces the
 * current-user narrowing on for this card only — so nothing here reads them.
 */
export type EntityListTransientFilters = Omittable<{
  onlyMe?: boolean;
  filterWhere?: Record<string, unknown> | null;
}>;

/** Row ordering for the project list. `age` is the triage order: longest since
 *  the last posted Update first, so the most-owed Follow up leads. */
export type EntityListSort = "created" | "updated" | "name" | "age";

/** Stable orderBy per sort — module-level so the query variables stay referentially
 *  stable across renders (an inline object would re-subscribe every render).
 *  `age` orders on the server's `ageDate` — the date the Follow up clock counts
 *  from — ascending, so the oldest Update (the most lapsed row) comes first. */
const ORDER_BY: Record<EntityListSort, Record<string, SortDirection>> = {
  created: { creationDate: "desc" },
  updated: { lastUpdateDate: "desc" },
  name: { name: "asc" },
  age: { ageDate: "asc" },
};

/**
 * The data layer for the `entity-list` (Project list) card: the ONE folded
 * `where` + `orderBy` its table pages over. Folds the card's Filters blob and
 * the ambient dashboard window exactly like the drilldown reader — the date
 * column follows the sort so the window narrows the same field the list is
 * ordered by — then AND-folds the card's search as a server `name contains`
 * predicate. The card never fetches rows itself: the shared `IssueTable` pages
 * every group through windowed server queries over this `where`, so the list
 * reaches EVERY project in scope — never a capped bulk chunk.
 *
 * Query ownership follows `useWidgetFilters`: the persisted config query folds into
 * `base` until the header publishes, then the visible header owns the query
 * predicate for this viewer/session; the header's Only-Me rides the same bag into
 * `useScopedWhere`. A card never hand-builds the final scoped `where`.
 */
/** Each sort's natural direction — the one `ORDER_BY` encodes. A caller
 *  that lets the reader flip it passes the other one. */
export function entityListSortDirection(sort: EntityListSort): SortDirection {
  return sort === "name" ? "asc" : sort === "age" ? "asc" : "desc";
}

/** `ORDER_BY[sort]` turned to face `direction`. Module-level cache per pair so
 *  the query variables stay referentially stable. */
const ORDER_BY_FLIPPED: Record<EntityListSort, Record<string, SortDirection>> = {
  created: { creationDate: "asc" },
  updated: { lastUpdateDate: "asc" },
  name: { name: "desc" },
  age: { ageDate: "desc" },
};

export function useEntityList(
  sort: EntityListSort,
  filters: unknown,
  search: string,
  /** The direction the rows face; absent, the sort's natural one. */
  direction?: SortDirection,
): {
  where: Record<string, unknown> | undefined;
  orderBy: Record<string, SortDirection>;
} {
  const { base, override } = useWidgetFilters(filters);
  // The window narrows the field the list is ordered by. `age` has no where
  // field of its own (the clock's date is not filterable), so it follows the
  // update date it tracks.
  const dateField =
    sort === "updated" || sort === "age"
      ? ("lastUpdateDate" as const)
      : ("creationDate" as const);

  const { where: scopedWhere } = useScopedWhere(base, { dateField, override });
  const trimmedSearch = search.trim();
  const where = useMemo(
    () =>
      trimmedSearch
        ? combineWhereConditions([scopedWhere, { name: { contains: trimmedSearch } }])
        : scopedWhere,
    [scopedWhere, trimmedSearch],
  );

  const natural = entityListSortDirection(sort);
  const orderBy = direction && direction !== natural ? ORDER_BY_FLIPPED[sort] : ORDER_BY[sort];
  return { where, orderBy };
}

/**
 * The exact scoped project count for the card's title adornment — the same
 * server aggregation the metric card reduces (grouping by `stageId` is 1:1
 * with a project, so summing every bucket is the exact total), never a fetch.
 */
export function useEntityListTotal(
  sort: EntityListSort,
  filters: unknown,
): { total: number; loading: boolean } {
  const { where } = useEntityList(sort, filters, "");
  const { counts, ready } = useIssueGroupCounts({ where: where ?? {}, groupBy: "stageId" });
  const total = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + n, 0),
    [counts],
  );
  return { total, loading: !ready };
}
